import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { FC, ReactNode } from 'react';
import {
  apiClearVocab,
  apiCreateVocab,
  apiDeleteVocab,
  apiListVocab,
  type VocabCreatePayload,
  type VocabEntry,
} from './vocab-store';
import { useTranslation } from '../../i18n/I18nContext';

/** How often to re-poll the vocab list while waiting for server-side
 *  Stanza enrichment of an article's entries. */
const ENRICHMENT_POLL_INTERVAL_MS = 15_000;
/** Cap per-article poll attempts. Beyond this we give up — typically
 *  the article's language isn't Stanza-supported, so grammar will
 *  never arrive. 40 × 15s = 10 minutes, covering long Stanza runs on
 *  larger articles. */
const MAX_ENRICHMENT_POLL_ATTEMPTS = 40;

interface VocabMethods {
  refresh: () => Promise<void>;
  addEntry: (payload: VocabCreatePayload) => Promise<void>;
  deleteEntry: (articleId: string, vocabId: string) => Promise<void>;
  clearAll: () => Promise<void>;
}

interface VocabData {
  entries: VocabEntry[];
  loading: boolean;
  error: string | null;
}

interface VocabContextValue extends VocabMethods, VocabData {}

// Two contexts so consumers that only need the (stable) methods —
// notably ChunkRenderer, which just calls addEntry on selection — don't
// re-render every time `entries` changes (which happens on every poll,
// add, and delete). The combined `useVocab()` hook stays for back-
// compat with consumers (e.g. VocabList) that legitimately need both.
const VocabMethodsContext = createContext<VocabMethods | null>(null);
const VocabDataContext = createContext<VocabData | null>(null);

export const VocabProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const { t } = useTranslation();
  const [entries, setEntries] = useState<VocabEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Per-articleId count of polls we've made while its entries still
  // lack grammar. Lives in a ref because we don't want it to drive
  // rendering — it's purely loop bookkeeping.
  const pollAttemptsRef = useRef<Map<string, number>>(new Map());

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const fresh = await apiListVocab();
      setEntries(fresh);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('vocab.load_failed'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Pick up server-side grammar backfill when the user returns to the tab.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') refresh();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [refresh]);

  // Per-article polling for server-side Stanza enrichment. Polls only
  // while there are articles with entries lacking grammar AND we haven't
  // hit the per-article attempt cap. Stops the moment all entries have
  // grammar — steady-state is silent.
  useEffect(() => {
    const articleHasMissing = new Map<string, boolean>();
    for (const entry of entries) {
      const cur = articleHasMissing.get(entry.articleId) ?? false;
      articleHasMissing.set(entry.articleId, cur || !entry.partOfSpeech);
    }

    // Drop counters for articles whose entries are now fully enriched
    // or that no longer appear (deleted). Counters survive grammar
    // arriving for one entry as long as another in the same article
    // is still missing — we re-evaluate per article, not per entry.
    for (const articleId of Array.from(pollAttemptsRef.current.keys())) {
      if (articleHasMissing.get(articleId) !== true) {
        pollAttemptsRef.current.delete(articleId);
      }
    }

    const articlesToPoll: string[] = [];
    for (const [articleId, hasMissing] of articleHasMissing) {
      if (!hasMissing) continue;
      const attempts = pollAttemptsRef.current.get(articleId) ?? 0;
      if (attempts < MAX_ENRICHMENT_POLL_ATTEMPTS) {
        articlesToPoll.push(articleId);
      }
    }

    if (articlesToPoll.length === 0) return;

    const timer = setTimeout(() => {
      for (const articleId of articlesToPoll) {
        pollAttemptsRef.current.set(
          articleId,
          (pollAttemptsRef.current.get(articleId) ?? 0) + 1,
        );
      }
      refresh();
    }, ENRICHMENT_POLL_INTERVAL_MS);

    return () => clearTimeout(timer);
  }, [entries, refresh]);

  const addEntry = useCallback(async (payload: VocabCreatePayload) => {
    const created = await apiCreateVocab(payload);
    // Reset the polling counter for this article — a freshly saved
    // entry restarts the grammar-wait clock for any prior entries
    // from the same article that had given up.
    pollAttemptsRef.current.delete(payload.articleId);
    setEntries(prev => {
      const others = prev.filter(e => e.vocabId !== created.vocabId);
      return [created, ...others];
    });
  }, []);

  const deleteEntry = useCallback(async (articleId: string, vocabId: string) => {
    // Optimistic: drop the entry immediately so the card disappears
    // without waiting for the API round-trip. On failure, re-insert it
    // (preserving the createdAt-desc order that listForUser returns) and
    // rethrow so the caller can toast.
    let removed: VocabEntry | undefined;
    setEntries(prev => {
      removed = prev.find(e => e.vocabId === vocabId);
      return prev.filter(e => e.vocabId !== vocabId);
    });
    try {
      await apiDeleteVocab(articleId, vocabId);
    } catch (err) {
      if (removed) {
        const r = removed;
        setEntries(prev => {
          if (prev.some(e => e.vocabId === r.vocabId)) return prev;
          const next = [...prev, r];
          next.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
          return next;
        });
      }
      throw err;
    }
  }, []);

  const clearAll = useCallback(async () => {
    await apiClearVocab();
    pollAttemptsRef.current.clear();
    setEntries([]);
  }, []);

  // Methods value is referentially stable across renders — every callback
  // it bundles is a useCallback with empty (or stable) deps. Consumers of
  // useVocabMethods() therefore never re-render due to vocab state churn.
  const methods = useMemo<VocabMethods>(
    () => ({ refresh, addEntry, deleteEntry, clearAll }),
    [refresh, addEntry, deleteEntry, clearAll],
  );

  const data = useMemo<VocabData>(
    () => ({ entries, loading, error }),
    [entries, loading, error],
  );

  return (
    <VocabMethodsContext.Provider value={methods}>
      <VocabDataContext.Provider value={data}>
        {children}
      </VocabDataContext.Provider>
    </VocabMethodsContext.Provider>
  );
};

export function useVocabMethods(): VocabMethods {
  const ctx = useContext(VocabMethodsContext);
  if (!ctx) throw new Error('useVocabMethods must be used inside a VocabProvider');
  return ctx;
}

export function useVocabData(): VocabData {
  const ctx = useContext(VocabDataContext);
  if (!ctx) throw new Error('useVocabData must be used inside a VocabProvider');
  return ctx;
}

/**
 * Combined accessor — re-renders on every entries/loading/error change.
 * Prefer `useVocabMethods()` if you only need mutators (most call sites);
 * prefer `useVocabData()` if you only need the entries list.
 */
export function useVocab(): VocabContextValue {
  const methods = useVocabMethods();
  const data = useVocabData();
  return useMemo(() => ({ ...methods, ...data }), [methods, data]);
}
